import {
  Alert,
  Flex,
  message,
  Radio,
  RadioChangeEvent,
  Tooltip
} from 'antd';
import { Button } from '../../../button';
import { Typography } from '../../../typography';
import {
  CompressOutlined,
  ExpandOutlined,
  SaveOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons';
import { useTheme } from '../../../../providers/theme';
import { getColors } from '../../../../components/utils/getColors';
import { useCallback, useState, useEffect } from 'react';
import ChartCorrelation from './components/chartCorrelation';
import { TLambdaCorrelation, TLiaoCorrelation } from '../../types';
import {
  FziFunctions,
  SelectedFziFunctions,
  SelectedFziFunctionsOptions
} from '../../../../@types/projects/types';
import { useSelected } from '../../../../providers/selectedContext';
import { useAppContext } from '../../../../providers/AppContext';
import { saveSelectedFziFunctions } from '../../../../api/data';
import ColorSettings from '../../../../components/chart/colorSettings';
import {
  CalculationStrategies,
  LambdaSampleResults,
  LiaoSampleResults,
  ProjectSample
} from '../../../../@types/samples/types';
import { transformChartData } from '../../utils/transformChartData';
import { useCorrelation } from '../../../../providers/Correlation';
import { useNotificationCenter } from '../../../../providers/NotificationCenter';
import { useDimensionControl } from '../../hook/useDimensionControl';

const CHART_STRATEGY_LIAO = CalculationStrategies.LIAO;
const CHART_STRATEGY_LAMBDA = CalculationStrategies.LAMBDA;

const HEIGHT_OPTIONS = ['60%', '70%', '80%', '90%', '100%'];
const WIDTH_OPTIONS = ['19%', '24%', '32%', '49%', '100%'];

const LIAO_FZI_FUNCTIONS = [
  'best_a',
  'best_b',
  'lambda_variable',
  'true_pc_max',
  'swir',
  'pce'
];
const LAMBDA_FZI_FUNCTIONS = ['pe', 'lambda_value', 'true_pc_max', 'swir'];

const transformLiaoField = (
  samples: ProjectSample[],
  field: keyof LiaoSampleResults,
  selectedXAxis: 'fzi' | 'rqi',
  color?: string,
) => {
  return samples.map(sample => {
    const fzi = sample.input_sample_values.fzi || 0;
    const code = sample.sample_code;
    const isSelected = sample.selectedAnalyses[CalculationStrategies.LIAO];
    const rawValue = sample.analysis_values.liao?.[field] ?? 0;
    const finalColor = isSelected ? color : 'rgba(100, 100, 100, 0.6)';
    const xAxisValue = selectedXAxis === 'fzi' ? fzi : sample.input_sample_values.rqi || 0;
    return transformChartData(rawValue, xAxisValue, code, isSelected, finalColor);
  });
};

const transformLambdaField = (
  samples: ProjectSample[],
  field: Exclude<keyof LambdaSampleResults, 'method'>,
  selectedXAxis: 'fzi' | 'rqi'
) => {
  const selectedColor = '#f47936';
  const notSelectedColor = 'rgba(100, 100, 100, 0.3)';
  return samples.map(sample => {
    const fzi = sample.input_sample_values.fzi || 0;
    const rqi = sample.input_sample_values.rqi || 0;
    const code = sample.sample_code;
    const isSelected = sample.selectedAnalyses[CalculationStrategies.LAMBDA];
    const rawValue = sample.analysis_values.lambda_values?.[field] ?? 0;
    const xAxisValue = selectedXAxis === 'fzi' ? fzi : rqi;
    return transformChartData(
      rawValue,
      xAxisValue,
      code,
      isSelected,
      isSelected ? selectedColor : notSelectedColor
    );
  });
};

export function CapillaryPressure() {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const {
    selectedLiaoSamples,
    selectedLambdaSamples,
    selectedXAxis,
    activeFziFunctions,
    syncProjectFromServer
  } = useCorrelation();
  const { registerDerivedNotification } = useNotificationCenter();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();

  const [chartColors, setChartColors] = useState({
    scatter: '#f47936',
    line: '#72E2D8'
  });
  const [loadingSave, setLoadingSave] = useState(false);
  const [showLambda, setShowLambda] = useState(false);
  const [localFziFunctions, setLocalFziFunctions] =
    useState<SelectedFziFunctions>({});
  const [warningSettings, setWarningSettings] = useState({
    show: false,
    liao: false,
    lambda: false
  });

  const selectedFziFunctions = activeFziFunctions;

  const {
    currentValue: chartHeight,
    increase: expand,
    decrease: compress
  } = useDimensionControl(HEIGHT_OPTIONS, 0, 'correlation.capillaryPressure.chartHeightIndex');
  const {
    currentValue: containerWidth,
    increase: zoomIn,
    decrease: zoomOut
  } = useDimensionControl(WIDTH_OPTIONS, 2, 'correlation.capillaryPressure.chartWidthIndex');

  function handleDataTypeChange(e: RadioChangeEvent) {
    setShowLambda(e.target.value);
  }

  const updateLocalSelectedFziFunctions = useCallback(
    (
      fnName: keyof SelectedFziFunctions,
      value: SelectedFziFunctionsOptions
    ) => {
      const mismatchs: typeof warningSettings = {
        show: false,
        lambda: false,
        liao: false
      };

      setLocalFziFunctions(old => {
        const updatedLocalFziFunctions = { ...old, [fnName]: value };
        Object.entries(updatedLocalFziFunctions).forEach(([key, value]) => {
          const selected = selectedFziFunctions?.[key]?.selected;
          if (selected !== value) {
            if (LIAO_FZI_FUNCTIONS.includes(key)) {
              mismatchs.liao = true;
              mismatchs.show = true;
            }
            if (LAMBDA_FZI_FUNCTIONS.includes(key)) {
              mismatchs.lambda = true;
              mismatchs.show = true;
            }
          }
        });
        return { ...old, [fnName]: value };
      });

      setWarningSettings(mismatchs);
    },
    [selectedFziFunctions]
  );

  const liaoData: TLiaoCorrelation = {
    bestA: transformLiaoField(
      selectedLiaoSamples,
      'best_a',
      selectedXAxis,
      chartColors.scatter,
    ),
    bestB: transformLiaoField(
      selectedLiaoSamples,
      'best_b',
      selectedXAxis,
      chartColors.scatter,
    ),
    lambda: transformLiaoField(
      selectedLiaoSamples,
      'lambda_variable',
      selectedXAxis,
      chartColors.scatter,
    ),
    swir: transformLiaoField(
      selectedLiaoSamples,
      'swir',
      selectedXAxis,
      chartColors.scatter,
    ),
    pce: transformLiaoField(
      selectedLiaoSamples,
      'pce',
      selectedXAxis,
      chartColors.scatter,
    ),
    truePcMax: transformLiaoField(
      selectedLiaoSamples,
      'true_pc_max',
      selectedXAxis,
      chartColors.scatter,
    ),
  };

  const lambdaData: TLambdaCorrelation = {
    lambdaValue: transformLambdaField(selectedLambdaSamples, 'lambda_value', selectedXAxis),
    pe: transformLambdaField(selectedLambdaSamples, 'pe', selectedXAxis),
    // Swir is the lab irreducible water saturation (the user's input, resolved
    // identically by both analyses). Read it from the Lambda analysis here so
    // the Brooks-Corey tab uses its own lab Swir instead of borrowing Liao's.
    swir: transformLambdaField(selectedLambdaSamples, 'swir', selectedXAxis)
  };

  async function handleSaveSelectedFziFunctions() {
    if(!selectedInfo.project?.uid) {
      return;
    }
    try {
      setLoadingSave(true);
      const scopedSelected = {
        best_a: localFziFunctions.best_a,
        best_b: localFziFunctions.best_b,
        lambda_variable: localFziFunctions.lambda_variable,
        true_pc_max: localFziFunctions.true_pc_max,
        swir: localFziFunctions.swir,
        pce: localFziFunctions.pce,
        pe: localFziFunctions.pe,
        lambda_value: localFziFunctions.lambda_value,
      };
      const payload = {
        x_variable: selectedXAxis,
        selected: scopedSelected,
        section: 'capillary_pressure' as const
      };
      const response = await saveSelectedFziFunctions(
        selectedInfo.project?.uid,
        payload
      );
      setSelectedInfo({ project: response });
      upsertProject(response);
      syncProjectFromServer(response);
      message.success('Selected FZI functions saved successfully');
      setWarningSettings({ show: false, liao: false, lambda: false });
    } catch (error) {
      console.error(error);
      message.error('Error saving the selected FZI functions');
    } finally {
      setLoadingSave(false);
    }
  }

  const getWarningMessage = useCallback(() => {
    if (!warningSettings.show) return '';
    if (warningSettings.lambda && warningSettings.liao) {
      return 'Unsaved changes in Liao and Brooks-Corey correlations';
    }

    if (warningSettings.lambda) {
      return 'Unsaved changes in Brooks-Corey correlation';
    }

    return 'Unsaved changes in Liao correlation';
  }, [warningSettings]);

  useEffect(() => {
    if (selectedFziFunctions) {
      const newLocalState: SelectedFziFunctions = {};

      (Object.keys(selectedFziFunctions) as Array<keyof FziFunctions>).forEach(key => {
        newLocalState[key] = selectedFziFunctions[key]?.selected ?? undefined;
      });

      setLocalFziFunctions(newLocalState);
    } else {
      setLocalFziFunctions({});
    }
  }, [selectedFziFunctions]);

  useEffect(() => {
    registerDerivedNotification(
      'capillary-fzi-unsaved',
      warningSettings.show
        ? {
            id: 'capillary-fzi-unsaved',
            kind: 'fzi-unsaved',
            severity: 'warning',
            title: 'Capillary pressure correlation has unsaved FZI changes',
            message: getWarningMessage(),
            count: 1,
            sampleCodes: [],
            createdAt: 'current',
            current: true,
            target: {
              type: 'section',
              view: 'correlation',
              anchorId: 'correlation-capillary-pressure'
            }
          }
        : null
    );

    return () => {
      registerDerivedNotification('capillary-fzi-unsaved', null);
    };
  }, [
    getWarningMessage,
    registerDerivedNotification,
    warningSettings.lambda,
    warningSettings.liao,
    warningSettings.show
  ]);

  return (
    <section id="capillary-pressure-correlation">
      <Typography.Heading level={4} style={{ padding: '1rem 1rem 0' }}>
        {`Capillary Pressure Correlation - ${selectedXAxis.toUpperCase()}`}
      </Typography.Heading>
      <Radio.Group
        onChange={handleDataTypeChange}
        options={[
          { label: 'Liao Correlation', value: false },
          { label: 'Brooks-Corey Correlation', value: true }
        ]}
        defaultValue={false}
        style={{
          width: '100%',
          padding: '1rem'
        }}
        block
        optionType="button"
        buttonStyle="solid"
        className={isDay ? 'day-radio' : 'night-radio'}
      />
      <div style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex' }}>
            <ColorSettings
              chartColors={chartColors}
              setChartColors={setChartColors}
            />
            <Tooltip title="Zoom in charts">
              <Button variant="ghost" size="sm" startIcon={<ZoomInOutlined />} onClick={zoomIn} />
            </Tooltip>
            <Tooltip title="Zoom out charts">
              <Button variant="ghost" size="sm" startIcon={<ZoomOutOutlined />} onClick={zoomOut} />
            </Tooltip>
            {warningSettings.show && (
              <Alert
                message={getWarningMessage()}
                type="warning"
                style={{
                  marginLeft: '0.5rem',
                  color: 'black',
                  backgroundColor: '#FFFF8F',
                  height: '2rem'
                }}
                showIcon
                closable
              />
            )}
          </div>
          <div style={{ display: 'flex' }}>
            <Tooltip title="Expand charts">
              <Button variant="ghost" size="sm" startIcon={<ExpandOutlined />} onClick={expand} />
            </Tooltip>
            <Tooltip title="Compress charts">
              <Button variant="ghost" size="sm" startIcon={<CompressOutlined />} onClick={compress} />
            </Tooltip>
            <Tooltip title="Update selected FZI functions">
              <Button variant="ghost" size="sm" startIcon={<SaveOutlined />} disabled={loadingSave} loading={loadingSave} onClick={handleSaveSelectedFziFunctions} />
            </Tooltip>
          </div>
        </div>

        <Flex
          wrap="wrap"
          gap="small"
          justify="space-between"
          hidden={showLambda}
        >
          <>
            <ChartCorrelation
              received={liaoData?.bestA || []}
              analysisType={CHART_STRATEGY_LIAO}
              field="best_a"
              name="Best A Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Best A"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={liaoData?.bestB || []}
              analysisType={CHART_STRATEGY_LIAO}
              field="best_b"
              name="Best B Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Best B"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={liaoData?.lambda || []}
              analysisType={CHART_STRATEGY_LIAO}
              field="lambda_variable"
              name="Liao Lambda Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Lambda"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={liaoData?.swir || []}
              analysisType={CHART_STRATEGY_LIAO}
              field="swir"
              name="Swir Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Swir"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={liaoData?.pce || []}
              analysisType={CHART_STRATEGY_LIAO}
              field="pce"
              name="PcE Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="PcE"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={liaoData?.truePcMax || []}
              analysisType={CHART_STRATEGY_LIAO}
              field="true_pc_max"
              name="Pc Max Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Pc Max"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
          </>
        </Flex>
        <Flex
          wrap="wrap"
          gap="small"
          justify="space-between"
          hidden={!showLambda}
        >
          <>
            <ChartCorrelation
              received={lambdaData?.pe || []}
              analysisType={CHART_STRATEGY_LAMBDA}
              field="pe"
              name="Pe Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Pe"
              chartType="line"
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={
                lambdaData?.lambdaValue?.map(point => ({
                  ...point,
                  y: point.y * -1
                })) || []
              }
              analysisType={CHART_STRATEGY_LAMBDA}
              field="lambda_value"
              name="Brooks-Corey Exponent Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Exponent magnitude (−m)"
              chartType="line"
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={lambdaData?.swir || []}
              analysisType={CHART_STRATEGY_LAMBDA}
              field="swir"
              name="Swir (lab) Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Swir (lab)"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
            <ChartCorrelation
              received={liaoData?.truePcMax || []}
              analysisType={CHART_STRATEGY_LAMBDA}
              field="true_pc_max"
              name="Pc Max Correlation"
              xAxis={selectedXAxis.toUpperCase()}
              yAxis="Pc Max"
              chartType={'line'}
              containerWidth={containerWidth}
              height={chartHeight}
              updateSelectedFziFunctions={updateLocalSelectedFziFunctions}
              chartColors={chartColors}
            />
          </>
        </Flex>
      </div>
    </section>
  );
}
